import {
  Directive,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output
} from "@angular/core";
import {
  DetachedDomElementService,
  DetachedElementRef
} from "src/app/services/detached-dom-element.service";
import { EditorOptions as TinyEditorOptions, Editor } from "tinymce";
import { initWysiwygEditor } from "src/app/support/tiny";

const LAYOUT_WAIT_TIMEOUT_MS = 3000;

@Directive({
  selector: "[tinyeditor]",
  standalone: false
})
export class TinyEditorDirective implements OnInit, OnDestroy {
  @Input()
  init: Partial<TinyEditorOptions> | undefined;

  @Input()
  maxImageSize?: number;

  @Input()
  containerMode: "detached" | "inside" = "detached";

  @Input()
  readOnly: boolean = false;

  @Output()
  contentChanged: EventEmitter<void> = new EventEmitter<void>();

  @Output()
  editorReady: EventEmitter<void> = new EventEmitter<void>();

  @Output()
  editorRendered: EventEmitter<void> = new EventEmitter<void>();

  private readonly detachedDomElementService = inject(
    DetachedDomElementService
  );
  private readonly elementRef: ElementRef<HTMLElement> = inject(ElementRef);

  private editor: Editor | undefined;
  private detachedElementRef: DetachedElementRef | undefined;
  private tinyContainer: HTMLElement | undefined;
  private suspendedContent: string | undefined;
  private isEditorSuspended = false;
  private isDestroyed = false;

  async ngOnInit(): Promise<void> {
    this.#ensureHost();
    await this.#createEditorInstance();
  }

  #ensureHost(): void {
    if (this.tinyContainer) {
      return;
    }

    switch (this.containerMode) {
      case "detached": {
        if (!this.detachedElementRef) {
          this.detachedElementRef = this.detachedDomElementService.install(
            this.elementRef.nativeElement
          );
        }

        this.tinyContainer = document.createElement("div");
        this.detachedElementRef.element.appendChild(this.tinyContainer);
        break;
      }
      case "inside": {
        this.tinyContainer = this.elementRef.nativeElement;
        break;
      }
    }
  }

  async #createEditorInstance(): Promise<void> {
    if (this.isDestroyed || this.editor || !this.tinyContainer) {
      return;
    }

    if (this.init) {
      this.init.readonly = this.readOnly;
    } else {
      this.init = {
        readonly: this.readOnly
      };
    }

    this.editor = await initWysiwygEditor(
      this.tinyContainer,
      () => {
        this.contentChanged.emit();
      },
      this.init,
      undefined,
      {
        imageMaxSize: this.maxImageSize
      }
    );

    if (this.#shouldDiscardEditorInstance()) {
      this.#discardEditorInstance();

      return;
    }

    this.editorReady.emit();
    await this.#waitUntilVisuallyReady();

    if (this.#shouldDiscardEditorInstance()) {
      this.#discardEditorInstance();

      return;
    }

    this.editorRendered.emit();
  }

  #shouldDiscardEditorInstance(): boolean {
    return this.isDestroyed || this.isEditorSuspended;
  }

  #discardEditorInstance(): void {
    if (!this.editor) {
      return;
    }

    this.suspendedContent = this.editor.getContent();
    this.#destroyEditorOnly();
  }

  #waitUntilVisuallyReady(): Promise<void> {
    return this.#waitUntilLaidOut().then(() => this.#waitForNextPaint());
  }

  #waitUntilLaidOut(): Promise<void> {
    const layoutRoot = this.#getLayoutRoot();
    const target = this.elementRef.nativeElement;

    if (this.#isTinyUiVisible(layoutRoot)) {
      return Promise.resolve();
    }

    return new Promise<void>((resolve) => {
      const finish = (): void => {
        window.clearTimeout(timeoutId);
        observer.disconnect();
        resolve();
      };

      const timeoutId = window.setTimeout(finish, LAYOUT_WAIT_TIMEOUT_MS);

      const observer = new ResizeObserver(() => {
        if (this.#isTinyUiVisible(layoutRoot)) {
          finish();
        }
      });

      observer.observe(target);

      if (layoutRoot !== target) {
        observer.observe(layoutRoot);
      }
    });
  }

  #getLayoutRoot(): HTMLElement {
    return this.detachedElementRef?.element ?? this.elementRef.nativeElement;
  }

  #isTinyUiVisible(layoutRoot: HTMLElement): boolean {
    if (!this.#hasLayoutSize(layoutRoot)) {
      return false;
    }

    const toxUi = layoutRoot.querySelector(".tox-tinymce");

    return toxUi instanceof HTMLElement && this.#hasLayoutSize(toxUi);
  }

  #hasLayoutSize(element: HTMLElement): boolean {
    const { width, height } = element.getBoundingClientRect();

    return width > 0 && height > 0;
  }

  #waitForNextPaint(): Promise<void> {
    return new Promise<void>((resolve) => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => resolve());
      });
    });
  }

  setContent(content: string): void {
    this.suspendedContent = content;

    this.editor?.setContent(content, {
      format: "html",
      initial: true,
      no_events: true
    });
  }

  ngOnDestroy(): void {
    this.destroied();
  }

  getContent(): string | undefined {
    if (this.editor) {
      return this.editor.getContent();
    }

    return this.suspendedContent;
  }

  activated(): void {
    if (this.detachedElementRef) {
      this.detachedDomElementService.show(this.detachedElementRef.uuid);
    }

    if (this.isEditorSuspended) {
      void this.#resumeEditor();
    }
  }

  decativated(): void {
    this.#suspendEditor();
  }

  destroied(): void {
    this.isDestroyed = true;
    this.#destroyEditorOnly();
    this.#destroyDetachedHost();
    this.suspendedContent = undefined;
    this.isEditorSuspended = false;
  }

  #suspendEditor(): void {
    if (this.isEditorSuspended || this.isDestroyed) {
      return;
    }

    if (this.editor) {
      this.suspendedContent = this.editor.getContent();
      this.#destroyEditorOnly();
    }

    if (this.detachedElementRef) {
      this.detachedDomElementService.hide(this.detachedElementRef.uuid);
    }

    this.isEditorSuspended = true;
  }

  async #resumeEditor(): Promise<void> {
    if (this.isDestroyed || !this.isEditorSuspended) {
      return;
    }

    this.isEditorSuspended = false;
    this.#destroyEditorOnly();

    if (this.tinyContainer) {
      this.tinyContainer.replaceChildren();
    }

    await this.#createEditorInstance();

    if (this.isDestroyed || this.isEditorSuspended) {
      return;
    }

    if (this.suspendedContent !== undefined) {
      this.setContent(this.suspendedContent);
    }
  }

  #destroyEditorOnly(): void {
    this.editor?.destroy(false);
    this.editor = undefined;
  }

  #destroyDetachedHost(): void {
    if (!this.detachedElementRef) {
      return;
    }

    this.detachedDomElementService.destory(this.detachedElementRef.uuid);
    this.detachedElementRef = undefined;
    this.tinyContainer = undefined;
  }
}
